import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { DndContext, useDraggable, useDroppable, DragOverlay, closestCenter, pointerWithin } from '@dnd-kit/core';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, RotateCcw, Star, Target, Info, Zap, Check, X, GripVertical, Timer, Flame, Trophy, ChevronRight, Sparkles, Award, ThumbsUp, ThumbsDown, Hash } from 'lucide-react';
import { LAB_CONTENT } from './labContent';
import { NEW_LAB_CONTENT } from './labContentNew';
import { MISSION_DESCRIPTIONS } from './missionDescriptions';
import { TimerChallengeActivity, StreakComboActivity, MemoryCardActivity, PuzzleActivity, RapidFireActivity } from './GamifiedActivities';
import { CatchGameActivity } from './CatchGame';
import { TargetShooterActivity } from './TargetShooter';
import { TeacherAvatar } from '../../components/TeacherAvatar';
import { useAuth } from '../../context/AuthContext';

const ALL_LAB_CONTENT = (() => {
  const merged = { ...LAB_CONTENT, ...NEW_LAB_CONTENT };
  // Merge detailed descriptions into missions
  Object.entries(merged).forEach(([labId, lab]) => {
    const descs = MISSION_DESCRIPTIONS[labId];
    if (descs) {
      lab.missions.forEach((mission) => {
        if (descs[mission.id]) {
          mission.description = descs[mission.id];
        }
      });
    }
  });
  // Normalize true-false data: some labs use 'items' instead of 'statements'
  Object.values(merged).forEach(lab => {
    lab.missions.forEach(m => {
      if (m.type === 'true-false' && m.items && !m.statements) {
        m.statements = m.items;
      }
    });
  });

  // Collect terms from all labs for game data generation
  const allLabTerms = {};
  Object.entries(merged).forEach(([labId, lab]) => {
    const terms = new Set();
    lab.missions.forEach(m => {
      if (m.items) m.items.forEach(item => {
        if (item.left) terms.add(item.left);
        if (item.right) terms.add(item.right);
        if (item.answer && typeof item.answer === 'string') terms.add(item.answer);
        if (item.name) terms.add(item.name);
      });
      if (m.categories && Array.isArray(m.categories)) m.categories.forEach(c => { if (typeof c === 'string') terms.add(c); });
      if (m.questions) m.questions.forEach(q => {
        const ans = typeof q.answer === 'number' ? q.options[q.answer] : q.answer;
        if (ans) terms.add(ans);
      });
    });
    allLabTerms[labId] = [...terms].filter(t => t && typeof t === 'string' && t.length > 1 && t.length < 25);
  });

  // Generate M11 (Catch Game) and M12 (Target Shooter) for each lab
  Object.entries(merged).forEach(([labId, lab]) => {
    const correctTerms = allLabTerms[labId] || [];
    const distractors = [];
    Object.entries(allLabTerms).forEach(([id, terms]) => {
      if (id !== labId) distractors.push(...terms.slice(0, 3));
    });
    const shuffledD = distractors.sort(() => Math.random() - 0.5).slice(0, Math.min(8, Math.ceil(correctTerms.length * 0.6)));
    const gameItems = [
      ...correctTerms.slice(0, 12).map(t => ({ term: t, correct: true })),
      ...shuffledD.map(t => ({ term: t, correct: false }))
    ];

    lab.missions.push({
      id: 11, name: `${lab.title} Catch Challenge`, type: 'catch-game',
      description: `Catch the correct terms related to ${lab.title} as they fall! Avoid terms from other subjects. Move the basket with your mouse.`,
      stars: 2, difficulty: 'Medium', gameItems
    });

    // Collect quiz-style questions for target shooter
    const shooterQs = [];
    lab.missions.forEach(m => {
      if (m.type === 'quiz' && m.questions) {
        m.questions.forEach(q => shooterQs.push({ question: q.question, options: q.options.slice(0, 4), answer: typeof q.answer === 'number' ? q.options[q.answer] : q.answer }));
      }
    });
    if (shooterQs.length < 3) {
      lab.missions.filter(m => m.type === 'match' && m.items).forEach(m => {
        m.items.forEach(item => {
          if (shooterQs.length < 6) {
            const wrongs = correctTerms.filter(t => t !== item.right).sort(() => Math.random() - 0.5).slice(0, 3);
            shooterQs.push({ question: `What matches "${item.left}"?`, options: [item.right, ...wrongs].sort(() => Math.random() - 0.5), answer: item.right });
          }
        });
      });
    }

    lab.missions.push({
      id: 12, name: `${lab.title} Target Practice`, type: 'target-shooter',
      description: `Aim your cannon and shoot the correct answers! Adjust angle and power to hit the right target.`,
      stars: 3, difficulty: 'Hard', questions: shooterQs.slice(0, 6)
    });
  });

  // Auto-gamify: convert ~50% of original 10 missions per lab to gamified types
  Object.values(merged).forEach(lab => {
    const converted = new Set();
    const conversions = [
      { from: 'quiz', to: 'timer-challenge' },
      { from: 'match', to: 'memory-card' },
      { from: 'fill-blank', to: 'puzzle' },
      { from: 'true-false', to: 'rapid-fire' },
    ];
    conversions.forEach(({ from, to }) => {
      const m = lab.missions.find(mi => mi.type === from && !converted.has(mi.id) && mi.id <= 10);
      if (m) { m.type = to; converted.add(m.id); }
    });
    // 5th conversion: streak-combo from next available quiz, match, or fill-blank
    const extra = lab.missions.find(m =>
      ['quiz', 'match', 'fill-blank'].includes(m.type) && !converted.has(m.id) && m.id <= 10
    );
    if (extra) { extra.originalType = extra.type; extra.type = 'streak-combo'; converted.add(extra.id); }
  });
  return merged;
})();
import { labProgressApi } from '../../lib/api';

// ========== DRAG HELPERS ==========
function DraggableItem({ id, children, disabled }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, disabled });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes}
      className={`${isDragging ? 'opacity-30 scale-95' : ''} transition-all duration-150`}
      style={{ touchAction: 'none' }}>
      {children}
    </div>
  );
}

function DroppableZone({ id, children, className }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`transition-all duration-200 ${isOver ? 'ring-2 ring-primary/50 scale-[1.02]' : ''} ${className || ''}`}>
      {children}
    </div>
  );
}

// ========== MATCH ACTIVITY ==========
function MatchActivity({ mission, onComplete }) {
  const [pairs, setPairs] = useState({});
  const [checked, setChecked] = useState(false);
  const [shuffledRight, setShuffledRight] = useState([]);
  const [activeDrag, setActiveDrag] = useState(null);

  useEffect(() => {
    setPairs({});
    setChecked(false);
    setShuffledRight([...mission.items].sort(() => Math.random() - 0.5));
  }, [mission]);

  const pairedRightIds = Object.values(pairs);
  const unpairedRight = shuffledRight.filter(i => !pairedRightIds.includes(i.id));

  const handleDragEnd = (e) => {
    setActiveDrag(null);
    const { active, over } = e;
    if (!over) return;
    const rightItemId = active.id.replace('drag-right-', '');
    const leftItemId = over.id.replace('drop-left-', '');
    if (rightItemId && leftItemId && !pairs[leftItemId]) {
      setPairs(prev => ({ ...prev, [leftItemId]: rightItemId }));
    }
  };

  const removePair = (leftId) => {
    if (checked) return;
    setPairs(prev => { const n = { ...prev }; delete n[leftId]; return n; });
  };

  const check = () => {
    setChecked(true);
    if (mission.items.every(item => pairs[item.id] === item.match) && Object.keys(pairs).length === mission.items.length) onComplete();
  };

  const reset = () => {
    setPairs({});
    setChecked(false);
    setShuffledRight([...mission.items].sort(() => Math.random() - 0.5));
  };

  return (
    <DndContext onDragStart={e => setActiveDrag(e.active.id)} onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
      <p className="text-sm text-muted-foreground mb-4">{mission.instruction || 'Drag items from right column to match with left:'}</p>
      <div className="grid grid-cols-5 gap-4">
        <div className="col-span-2 space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">{mission.leftLabel || 'Items'}</h4>
          {mission.items.map(item => {
            const pairedId = pairs[item.id];
            const pairedItem = pairedId ? shuffledRight.find(r => r.id === pairedId) : null;
            const isCorrect = checked && pairedId === item.match;
            const isWrong = checked && pairedId && pairedId !== item.match;
            return (
              <DroppableZone key={item.id} id={`drop-left-${item.id}`}
                className={`px-4 py-3 rounded-xl border-2 border-dashed min-h-[52px] flex items-center justify-between transition-all ${
                  isCorrect ? 'border-green-500/50 bg-green-500/10' :
                  isWrong ? 'border-red-500/50 bg-red-500/10' :
                  pairedItem ? 'border-primary/30 bg-primary/5 border-solid' :
                  'border-border/40 bg-card/50 hover:border-primary/20'
                }`}>
                <span className="font-medium text-sm">{item.left}</span>
                {pairedItem && (
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs cursor-pointer hover:bg-destructive/20" onClick={() => removePair(item.id)}>
                      {pairedItem.right} {!checked && '×'}
                    </Badge>
                    {checked && (isCorrect ? <Check className="w-4 h-4 text-green-400" /> : <X className="w-4 h-4 text-red-400" />)}
                  </motion.div>
                )}
                {!pairedItem && !checked && <span className="text-xs text-muted-foreground/40">Drop here</span>}
              </DroppableZone>
            );
          })}
        </div>
        <div className="flex items-center justify-center">
          <div className="w-px h-full bg-gradient-to-b from-transparent via-border/30 to-transparent" />
        </div>
        <div className="col-span-2 space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">{mission.rightLabel || 'Drag to match'}</h4>
          <AnimatePresence>
            {unpairedRight.map(item => (
              <motion.div key={item.id} layout exit={{ opacity: 0, x: -20, scale: 0.8 }} transition={{ duration: 0.2 }}>
                <DraggableItem id={`drag-right-${item.id}`} disabled={checked}>
                  <div className={`px-4 py-3 rounded-xl border text-sm cursor-grab active:cursor-grabbing transition-all
                    border-border/50 bg-card hover:border-primary/40 hover:shadow-md hover:shadow-primary/5
                    ${activeDrag === `drag-right-${item.id}` ? 'ring-2 ring-primary' : ''}`}
                    data-testid={`match-right-${item.id}`}>
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-3 h-3 text-muted-foreground/40" />
                      <span>{item.right}</span>
                    </div>
                  </div>
                </DraggableItem>
              </motion.div>
            ))}
          </AnimatePresence>
          {unpairedRight.length === 0 && !checked && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-muted-foreground text-center py-4">All matched!</motion.p>
          )}
        </div>
      </div>
      <div className="flex gap-2 mt-5">
        <Button onClick={check} disabled={Object.keys(pairs).length !== mission.items.length || checked} data-testid="check-match-btn">
          <Check className="w-4 h-4 mr-2" /> Check Matches
        </Button>
        <Button variant="outline" onClick={reset}><RotateCcw className="w-4 h-4 mr-2" /> Reset</Button>
        {checked && mission.items.every(i => pairs[i.id] === i.match) && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex items-center gap-1 text-green-400 text-sm font-semibold ml-2">
            <Trophy className="w-4 h-4" /> Perfect!
          </motion.div>
        )}
      </div>
      <DragOverlay>
        {activeDrag ? (
          <div className="px-4 py-3 rounded-xl border border-primary bg-card shadow-xl shadow-primary/10 text-sm">
            {shuffledRight.find(i => `drag-right-${i.id}` === activeDrag)?.right}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// ========== ORDER ACTIVITY ==========
function OrderActivity({ mission, onComplete }) {
  const [items, setItems] = useState([]);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    setItems([...mission.items].sort(() => Math.random() - 0.5));
    setChecked(false);
  }, [mission]);

  const check = () => {
    setChecked(true);
    if (items.every((item, i) => item.order === i + 1)) onComplete();
  };

  const correctCount = checked ? items.filter((item, i) => item.order === i + 1).length : 0;

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">{mission.instruction || 'Drag items to arrange in the correct order:'}</p>
      <Reorder.Group axis="y" values={items} onReorder={checked ? () => {} : setItems} className="space-y-2">
        {items.map((item, idx) => {
          const isCorrect = checked && item.order === idx + 1;
          const isWrong = checked && item.order !== idx + 1;
          return (
            <Reorder.Item key={item.id} value={item}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-grab active:cursor-grabbing select-none transition-colors ${
                isCorrect ? 'border-green-500/50 bg-green-500/10' :
                isWrong ? 'border-red-500/50 bg-red-500/10' :
                'border-border/50 bg-card hover:border-primary/30 hover:shadow-sm'
              }`}
              whileDrag={{ scale: 1.03, boxShadow: '0 10px 30px rgba(0,0,0,0.2)', zIndex: 50 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}>
              <motion.div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                isCorrect ? 'bg-green-500 text-white' : isWrong ? 'bg-red-500 text-white' : 'bg-muted text-muted-foreground'
              }`} animate={isCorrect ? { scale: [1, 1.3, 1] } : {}} transition={{ duration: 0.3 }}>
                {idx + 1}
              </motion.div>
              <GripVertical className="w-4 h-4 text-muted-foreground/30 shrink-0" />
              <span className="text-sm flex-1">{item.text}</span>
              {checked && (
                isCorrect ? <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}><Check className="w-5 h-5 text-green-400" /></motion.div> :
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}><X className="w-5 h-5 text-red-400" /></motion.div>
              )}
            </Reorder.Item>
          );
        })}
      </Reorder.Group>
      {checked && (
        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="mt-3 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 text-sm">
            <span className="font-medium">{correctCount}/{items.length} correct</span>
            <div className="flex gap-0.5">
              {items.map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full ${items[i].order === i + 1 ? 'bg-green-500' : 'bg-red-500'}`} />
              ))}
            </div>
          </div>
        </motion.div>
      )}
      <div className="flex gap-2 mt-5">
        <Button onClick={check} disabled={checked} data-testid="check-order-btn"><Check className="w-4 h-4 mr-2" /> Check Order</Button>
        <Button variant="outline" onClick={() => { setItems([...mission.items].sort(() => Math.random() - 0.5)); setChecked(false); }}>
          <RotateCcw className="w-4 h-4 mr-2" /> Shuffle
        </Button>
      </div>
    </div>
  );
}

// ========== CATEGORIZE ACTIVITY ==========
function CategorizeActivity({ mission, onComplete }) {
  const [unplaced, setUnplaced] = useState([]);
  const [buckets, setBuckets] = useState({});
  const [checked, setChecked] = useState(false);
  const [activeDrag, setActiveDrag] = useState(null);

  useEffect(() => {
    setUnplaced([...mission.items].sort(() => Math.random() - 0.5));
    const cats = {};
    mission.categories.forEach(c => { cats[c.id] = []; });
    setBuckets(cats);
    setChecked(false);
  }, [mission]);

  const handleDragEnd = (e) => {
    setActiveDrag(null);
    const { active, over } = e;
    if (!over) return;
    const itemId = active.id.replace('cat-drag-', '');
    const catId = over.id.replace('cat-drop-', '');
    let item = unplaced.find(i => i.id === itemId);
    if (item) {
      setUnplaced(prev => prev.filter(i => i.id !== itemId));
      setBuckets(prev => ({ ...prev, [catId]: [...(prev[catId] || []), item] }));
    } else {
      for (const [bId, bItems] of Object.entries(buckets)) {
        const found = bItems.find(i => i.id === itemId);
        if (found) {
          setBuckets(prev => ({
            ...prev,
            [bId]: prev[bId].filter(i => i.id !== itemId),
            [catId]: [...(prev[catId] || []), found],
          }));
          break;
        }
      }
    }
  };

  const returnToPool = (catId, itemId) => {
    if (checked) return;
    const item = buckets[catId]?.find(i => i.id === itemId);
    if (item) {
      setBuckets(prev => ({ ...prev, [catId]: prev[catId].filter(i => i.id !== itemId) }));
      setUnplaced(prev => [...prev, item]);
    }
  };

  const check = () => {
    setChecked(true);
    let allCorrect = true;
    Object.entries(buckets).forEach(([catId, items]) => {
      items.forEach(item => { if (item.category !== catId) allCorrect = false; });
    });
    if (allCorrect && unplaced.length === 0) onComplete();
  };

  const reset = () => {
    setUnplaced([...mission.items].sort(() => Math.random() - 0.5));
    const cats = {};
    mission.categories.forEach(c => { cats[c.id] = []; });
    setBuckets(cats);
    setChecked(false);
  };

  const dragItem = activeDrag ? (
    unplaced.find(i => `cat-drag-${i.id}` === activeDrag) ||
    Object.values(buckets).flat().find(i => `cat-drag-${i.id}` === activeDrag)
  ) : null;

  return (
    <DndContext onDragStart={e => setActiveDrag(e.active.id)} onDragEnd={handleDragEnd} collisionDetection={pointerWithin}>
      <p className="text-sm text-muted-foreground mb-4">{mission.instruction || 'Drag items into the correct category:'}</p>
      <div className="mb-5 p-4 rounded-2xl bg-muted/20 border border-border/30 min-h-[70px]">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold text-muted-foreground">Items to sort</h4>
          <Badge variant="outline" className="text-xs">{unplaced.length} remaining</Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          <AnimatePresence>
            {unplaced.map(item => (
              <motion.div key={item.id} layout exit={{ scale: 0.5, opacity: 0 }} transition={{ duration: 0.15 }}>
                <DraggableItem id={`cat-drag-${item.id}`} disabled={checked}>
                  <div className="px-3 py-2 rounded-lg border border-border/50 bg-card text-xs font-medium cursor-grab active:cursor-grabbing
                    hover:border-primary/40 hover:shadow-md hover:shadow-primary/5 transition-all select-none flex items-center gap-1.5"
                    data-testid={`cat-item-${item.id}`}>
                    <GripVertical className="w-3 h-3 text-muted-foreground/30" />
                    {item.text}
                  </div>
                </DraggableItem>
              </motion.div>
            ))}
          </AnimatePresence>
          {unplaced.length === 0 && <span className="text-xs text-green-400 font-medium">All items placed!</span>}
        </div>
      </div>
      <div className={`grid gap-4 ${mission.categories.length === 2 ? 'grid-cols-2' : mission.categories.length >= 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-3'}`}>
        {mission.categories.map(cat => (
          <DroppableZone key={cat.id} id={`cat-drop-${cat.id}`}
            className={`p-4 rounded-2xl border-2 border-dashed min-h-[140px] transition-all ${
              checked ? 'border-border/30' : 'border-border/30 hover:border-primary/20'
            }`}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full" style={{ background: cat.color || '#888' }} />
              <h4 className="text-xs font-bold" style={{ color: cat.color || 'inherit' }}>{cat.label}</h4>
              <Badge variant="outline" className="text-[10px] ml-auto">{buckets[cat.id]?.length || 0}</Badge>
            </div>
            <div className="flex flex-wrap gap-1.5 min-h-[40px]">
              <AnimatePresence>
                {buckets[cat.id]?.map(item => {
                  const isCorrect = checked && item.category === cat.id;
                  const isWrong = checked && item.category !== cat.id;
                  return (
                    <motion.div key={item.id} initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.5, opacity: 0 }} layout>
                      {checked ? (
                        <span className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border inline-flex items-center gap-1 ${
                          isCorrect ? 'border-green-500/40 bg-green-500/10 text-green-400' : 'border-red-500/40 bg-red-500/10 text-red-400'
                        }`}>
                          {item.text}
                          {isCorrect ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        </span>
                      ) : (
                        <DraggableItem id={`cat-drag-${item.id}`}>
                          <span className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-border/50 bg-card
                            cursor-grab active:cursor-grabbing hover:border-destructive/40 transition-all inline-flex items-center gap-1 select-none"
                            onClick={() => returnToPool(cat.id, item.id)}>
                            <GripVertical className="w-2.5 h-2.5 text-muted-foreground/30" />
                            {item.text}
                          </span>
                        </DraggableItem>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
              {(!buckets[cat.id] || buckets[cat.id].length === 0) && !checked && (
                <span className="text-[10px] text-muted-foreground/30 py-2">Drop items here</span>
              )}
            </div>
          </DroppableZone>
        ))}
      </div>
      <div className="flex gap-2 mt-5">
        <Button onClick={check} disabled={unplaced.length > 0 || checked} data-testid="check-categories-btn">
          <Check className="w-4 h-4 mr-2" /> Check
        </Button>
        <Button variant="outline" onClick={reset}><RotateCcw className="w-4 h-4 mr-2" /> Reset</Button>
        {checked && unplaced.length === 0 && (() => {
          const allCorrect = mission.items.every(item => {
            const bucket = Object.entries(buckets).find(([_, items]) => items.find(i => i.id === item.id));
            return bucket && bucket[0] === item.category;
          });
          return allCorrect ? (
            <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-green-400 text-sm font-semibold flex items-center gap-1 ml-2">
              <Trophy className="w-4 h-4" /> All correct!
            </motion.span>
          ) : (
            <span className="text-red-400 text-sm ml-2">Some items are in the wrong category</span>
          );
        })()}
      </div>
      <DragOverlay>
        {dragItem ? (
          <div className="px-3 py-2 rounded-lg border border-primary bg-card shadow-xl shadow-primary/10 text-xs font-medium">
            {dragItem.text}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// ========== QUIZ ACTIVITY ==========
function QuizActivity({ mission, onComplete }) {
  const [currentQ, setCurrentQ] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [done, setDone] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15);
  const timerRef = useRef(null);

  useEffect(() => {
    setCurrentQ(0); setScore(0); setStreak(0); setBestStreak(0);
    setSelected(null); setShowResult(false); setDone(false); setTimeLeft(15);
  }, [mission]);

  useEffect(() => {
    if (done || showResult) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); handleTimeout(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQ, done, showResult]);

  const handleTimeout = () => { setShowResult(true); setStreak(0); setTimeout(() => advance(), 1500); };

  const advance = () => {
    if (currentQ + 1 < mission.questions.length) {
      setCurrentQ(c => c + 1); setSelected(null); setShowResult(false); setTimeLeft(15);
    } else {
      setDone(true);
      if (score >= mission.questions.length * 0.6) onComplete();
    }
  };

  const handleAnswer = (answer) => {
    if (showResult) return;
    clearInterval(timerRef.current);
    setSelected(answer); setShowResult(true);
    const isCorrect = answer === mission.questions[currentQ].answer;
    if (isCorrect) {
      setScore(s => s + 1);
      setStreak(s => { const n = s + 1; if (n > bestStreak) setBestStreak(n); return n; });
    } else { setStreak(0); }
    setTimeout(() => advance(), 1200);
  };

  const restart = () => {
    setCurrentQ(0); setScore(0); setStreak(0); setBestStreak(0);
    setSelected(null); setShowResult(false); setDone(false); setTimeLeft(15);
  };

  if (done) {
    const passed = score >= mission.questions.length * 0.6;
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-10">
        <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.5 }}
          className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${passed ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
          {passed ? <Trophy className="w-10 h-10 text-green-400" /> : <X className="w-10 h-10 text-red-400" />}
        </motion.div>
        <h3 className="text-xl font-bold mb-1">{passed ? 'Well Done!' : 'Keep Practicing!'}</h3>
        <p className="text-muted-foreground text-sm mb-2">Score: {score}/{mission.questions.length}</p>
        <div className="flex items-center justify-center gap-4 mb-6">
          <div className="text-center"><div className="text-lg font-bold text-primary">{bestStreak}</div><div className="text-xs text-muted-foreground">Best Streak</div></div>
          <div className="w-px h-8 bg-border" />
          <div className="text-center"><div className="text-lg font-bold">{Math.round(score / mission.questions.length * 100)}%</div><div className="text-xs text-muted-foreground">Accuracy</div></div>
        </div>
        <Button onClick={restart}><RotateCcw className="w-4 h-4 mr-2" /> Try Again</Button>
      </motion.div>
    );
  }

  const question = mission.questions[currentQ];
  const timerColor = timeLeft > 10 ? 'text-green-400' : timeLeft > 5 ? 'text-yellow-400' : 'text-red-400';

  return (
    <div>
      <div className="flex items-center justify-between mb-6 p-3 rounded-xl bg-muted/30">
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-xs">Q{currentQ + 1}/{mission.questions.length}</Badge>
          {streak >= 2 && (
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex items-center gap-1 text-orange-400 text-xs font-bold">
              <Flame className="w-3.5 h-3.5" /> {streak} streak!
            </motion.div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-1 text-sm font-mono font-bold ${timerColor}`}>
            <Timer className="w-4 h-4" /> {timeLeft}s
          </div>
          <Badge variant="secondary" className="text-xs">Score: {score}</Badge>
        </div>
      </div>
      <div className="w-full h-1.5 rounded-full bg-muted/50 mb-6 overflow-hidden">
        <motion.div className="h-full bg-primary rounded-full" animate={{ width: `${(currentQ / mission.questions.length) * 100}%` }} />
      </div>
      <motion.h3 key={currentQ} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
        className="text-base font-semibold mb-6">{question.question}</motion.h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {question.options.map((opt, i) => (
          <motion.button key={opt} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            onClick={() => handleAnswer(opt)} disabled={showResult}
            className={`px-5 py-4 rounded-xl border text-sm text-left transition-all ${
              showResult && opt === question.answer ? 'border-green-500 bg-green-500/15 text-green-400 scale-[1.02]' :
              showResult && opt === selected && opt !== question.answer ? 'border-red-500 bg-red-500/15 text-red-400' :
              !showResult ? 'border-border/50 bg-card hover:border-primary/40 hover:shadow-sm cursor-pointer active:scale-[0.98]' :
              'border-border/30 bg-card opacity-40'
            }`} data-testid={`quiz-option-${i}`}>
            <div className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-muted/50 flex items-center justify-center text-xs font-bold shrink-0">{String.fromCharCode(65 + i)}</span>
              <span>{opt}</span>
              {showResult && opt === question.answer && <Check className="w-4 h-4 text-green-400 ml-auto" />}
              {showResult && opt === selected && opt !== question.answer && <X className="w-4 h-4 text-red-400 ml-auto" />}
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// ========== NEW: FILL-IN-THE-BLANK ACTIVITY ==========
function FillBlankActivity({ mission, onComplete }) {
  const [placements, setPlacements] = useState({});
  const [checked, setChecked] = useState(false);
  const [activeDrag, setActiveDrag] = useState(null);
  const [shuffledOptions, setShuffledOptions] = useState([]);

  useEffect(() => {
    setPlacements({});
    setChecked(false);
    const allOptions = [
      ...mission.items.map(i => ({ id: i.id, text: i.answer })),
      ...(mission.distractors || []).map((d, idx) => ({ id: `dist-${idx}`, text: d })),
    ];
    setShuffledOptions(allOptions.sort(() => Math.random() - 0.5));
  }, [mission]);

  const usedOptionIds = Object.values(placements);
  const availableOptions = shuffledOptions.filter(o => !usedOptionIds.includes(o.id));

  const handleDragEnd = (e) => {
    setActiveDrag(null);
    const { active, over } = e;
    if (!over) return;
    const optionId = active.id.replace('fb-drag-', '');
    const blankId = over.id.replace('fb-drop-', '');
    if (blankId && !placements[blankId]) {
      setPlacements(prev => ({ ...prev, [blankId]: optionId }));
    }
  };

  const removePlacement = (blankId) => {
    if (checked) return;
    setPlacements(prev => { const n = { ...prev }; delete n[blankId]; return n; });
  };

  const check = () => {
    setChecked(true);
    const allCorrect = mission.items.every(item => {
      const placedOptId = placements[item.id];
      const placedOpt = shuffledOptions.find(o => o.id === placedOptId);
      return placedOpt && placedOpt.text === item.answer;
    });
    if (allCorrect && Object.keys(placements).length === mission.items.length) onComplete();
  };

  const reset = () => {
    setPlacements({});
    setChecked(false);
    setShuffledOptions(prev => [...prev].sort(() => Math.random() - 0.5));
  };

  const getPlacedOption = (blankId) => {
    const optId = placements[blankId];
    return optId ? shuffledOptions.find(o => o.id === optId) : null;
  };

  const isItemCorrect = (item) => {
    const placed = getPlacedOption(item.id);
    return placed && placed.text === item.answer;
  };

  const dragOpt = activeDrag ? shuffledOptions.find(o => `fb-drag-${o.id}` === activeDrag) : null;

  return (
    <DndContext onDragStart={e => setActiveDrag(e.active.id)} onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
      <p className="text-sm text-muted-foreground mb-4">{mission.instruction || 'Drag the correct words to fill in each blank:'}</p>

      {/* Word pool */}
      <div className="mb-6 p-4 rounded-2xl bg-muted/20 border border-border/30">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5"><Hash className="w-3 h-3" /> Word Bank</h4>
          <Badge variant="outline" className="text-xs">{availableOptions.length} left</Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          <AnimatePresence>
            {availableOptions.map(opt => (
              <motion.div key={opt.id} layout exit={{ scale: 0.5, opacity: 0 }} transition={{ duration: 0.15 }}>
                <DraggableItem id={`fb-drag-${opt.id}`} disabled={checked}>
                  <div className={`px-4 py-2.5 rounded-xl border text-sm font-medium cursor-grab active:cursor-grabbing transition-all select-none
                    bg-gradient-to-br from-card to-muted/30 border-border/50 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10 hover:-translate-y-0.5
                    ${activeDrag === `fb-drag-${opt.id}` ? 'ring-2 ring-primary shadow-lg' : ''}`}
                    data-testid={`fb-word-${opt.id}`}>
                    <div className="flex items-center gap-1.5">
                      <GripVertical className="w-3 h-3 text-muted-foreground/40" />
                      {opt.text}
                    </div>
                  </div>
                </DraggableItem>
              </motion.div>
            ))}
          </AnimatePresence>
          {availableOptions.length === 0 && !checked && <span className="text-xs text-green-400 font-medium">All words placed!</span>}
        </div>
      </div>

      {/* Sentences with blanks */}
      <div className="space-y-3">
        {mission.items.map((item, idx) => {
          const placed = getPlacedOption(item.id);
          const correct = checked && isItemCorrect(item);
          const wrong = checked && placed && !isItemCorrect(item);

          const parts = item.sentence.split('___');
          return (
            <motion.div key={item.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className={`p-4 rounded-xl border-2 transition-all ${
                correct ? 'border-green-500/40 bg-green-500/5' :
                wrong ? 'border-red-500/40 bg-red-500/5' :
                'border-border/30 bg-card/50'
              }`}>
              <div className="flex items-start gap-2">
                <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">{idx + 1}</span>
                <div className="text-sm leading-relaxed flex-1 flex flex-wrap items-center gap-1">
                  {parts.map((part, pi) => (
                    <React.Fragment key={pi}>
                      <span>{part}</span>
                      {pi < parts.length - 1 && (
                        <DroppableZone id={`fb-drop-${item.id}`}
                          className={`inline-flex min-w-[100px] h-9 rounded-lg border-2 border-dashed items-center justify-center mx-1 px-2 ${
                            correct ? 'border-green-500/50 bg-green-500/10' :
                            wrong ? 'border-red-500/50 bg-red-500/10' :
                            placed ? 'border-primary/40 bg-primary/5 border-solid' :
                            'border-border/40 hover:border-primary/30'
                          }`}>
                          {placed ? (
                            <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }}
                              className="text-sm font-semibold flex items-center gap-1 cursor-pointer"
                              onClick={() => removePlacement(item.id)}>
                              {placed.text}
                              {!checked && <X className="w-3 h-3 text-muted-foreground/50 hover:text-destructive" />}
                              {correct && <Check className="w-3.5 h-3.5 text-green-400" />}
                              {wrong && <X className="w-3.5 h-3.5 text-red-400" />}
                            </motion.span>
                          ) : (
                            <span className="text-xs text-muted-foreground/40">drop here</span>
                          )}
                        </DroppableZone>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              {wrong && <p className="text-xs text-green-400/80 mt-2 ml-8">Answer: {item.answer}</p>}
            </motion.div>
          );
        })}
      </div>

      <div className="flex gap-2 mt-5">
        <Button onClick={check} disabled={Object.keys(placements).length !== mission.items.length || checked} data-testid="check-fill-blank-btn">
          <Check className="w-4 h-4 mr-2" /> Check Answers
        </Button>
        <Button variant="outline" onClick={reset}><RotateCcw className="w-4 h-4 mr-2" /> Reset</Button>
        {checked && mission.items.every(i => isItemCorrect(i)) && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex items-center gap-1 text-green-400 text-sm font-semibold ml-2">
            <Trophy className="w-4 h-4" /> Perfect!
          </motion.div>
        )}
      </div>

      <DragOverlay>
        {dragOpt ? (
          <div className="px-4 py-2.5 rounded-xl border border-primary bg-card shadow-xl shadow-primary/10 text-sm font-medium">
            {dragOpt.text}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// ========== NEW: TRUE/FALSE RAPID FIRE ==========
function TrueFalseActivity({ mission, onComplete }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [answered, setAnswered] = useState(null);
  const [done, setDone] = useState(false);
  const [timeLeft, setTimeLeft] = useState(8);
  const [results, setResults] = useState([]);
  const timerRef = useRef(null);

  useEffect(() => {
    setCurrentIdx(0); setScore(0); setStreak(0); setBestStreak(0);
    setAnswered(null); setDone(false); setTimeLeft(8); setResults([]);
  }, [mission]);

  useEffect(() => {
    if (done || answered !== null) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); handleAnswer(null); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx, done, answered]);

  const advance = () => {
    if (currentIdx + 1 < mission.statements.length) {
      setCurrentIdx(c => c + 1); setAnswered(null); setTimeLeft(8);
    } else {
      setDone(true);
      if (score >= mission.statements.length * 0.6) onComplete();
    }
  };

  const handleAnswer = (answer) => {
    if (answered !== null) return;
    clearInterval(timerRef.current);
    const statement = mission.statements[currentIdx];
    const isCorrect = answer === statement.answer;
    setAnswered(answer);
    setResults(prev => [...prev, { correct: isCorrect, userAnswer: answer }]);
    if (isCorrect) {
      setScore(s => s + 1);
      setStreak(s => { const n = s + 1; if (n > bestStreak) setBestStreak(n); return n; });
    } else { setStreak(0); }
    setTimeout(() => advance(), 1000);
  };

  const restart = () => {
    setCurrentIdx(0); setScore(0); setStreak(0); setBestStreak(0);
    setAnswered(null); setDone(false); setTimeLeft(8); setResults([]);
  };

  if (done) {
    const passed = score >= mission.statements.length * 0.6;
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-8">
        <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.5 }}
          className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${passed ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
          {passed ? <Trophy className="w-10 h-10 text-green-400" /> : <X className="w-10 h-10 text-red-400" />}
        </motion.div>
        <h3 className="text-xl font-bold mb-1">{passed ? 'Great Reflexes!' : 'Keep Practicing!'}</h3>
        <p className="text-muted-foreground text-sm mb-4">Score: {score}/{mission.statements.length}</p>
        <div className="flex items-center justify-center gap-4 mb-4">
          <div className="text-center"><div className="text-lg font-bold text-primary">{bestStreak}</div><div className="text-xs text-muted-foreground">Best Streak</div></div>
          <div className="w-px h-8 bg-border" />
          <div className="text-center"><div className="text-lg font-bold">{Math.round(score / mission.statements.length * 100)}%</div><div className="text-xs text-muted-foreground">Accuracy</div></div>
        </div>
        <div className="flex flex-wrap gap-1 justify-center mb-6">
          {results.map((r, i) => (
            <div key={i} className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${r.correct ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              {r.correct ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
            </div>
          ))}
        </div>
        <Button onClick={restart}><RotateCcw className="w-4 h-4 mr-2" /> Try Again</Button>
      </motion.div>
    );
  }

  const statement = mission.statements[currentIdx];
  const isCorrect = answered === statement.answer;
  const timerColor = timeLeft > 5 ? 'text-green-400' : timeLeft > 3 ? 'text-yellow-400' : 'text-red-400';

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">{mission.instruction || 'Decide if each statement is True or False!'}</p>
      <div className="flex items-center justify-between mb-4 p-3 rounded-xl bg-muted/30">
        <Badge variant="outline" className="text-xs">{currentIdx + 1}/{mission.statements.length}</Badge>
        {streak >= 2 && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex items-center gap-1 text-orange-400 text-xs font-bold">
            <Flame className="w-3.5 h-3.5" /> {streak} streak!
          </motion.div>
        )}
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-1 text-sm font-mono font-bold ${timerColor}`}>
            <Timer className="w-4 h-4" /> {timeLeft}s
          </div>
          <Badge variant="secondary" className="text-xs">Score: {score}</Badge>
        </div>
      </div>
      <div className="w-full h-1.5 rounded-full bg-muted/50 mb-8 overflow-hidden">
        <motion.div className="h-full bg-primary rounded-full" animate={{ width: `${(currentIdx / mission.statements.length) * 100}%` }} />
      </div>

      <motion.div key={currentIdx} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className={`p-6 rounded-2xl border-2 text-center mb-8 transition-colors ${
          answered !== null ? (isCorrect ? 'border-green-500/40 bg-green-500/5' : 'border-red-500/40 bg-red-500/5') : 'border-border/30 bg-card'
        }`}>
        <p className="text-lg font-medium leading-relaxed">{statement.text}</p>
        {answered !== null && !isCorrect && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-muted-foreground mt-2">
            Correct answer: <span className="font-semibold text-foreground">{statement.answer ? 'True' : 'False'}</span>
          </motion.p>
        )}
      </motion.div>

      <div className="flex justify-center gap-6">
        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
          onClick={() => handleAnswer(true)} disabled={answered !== null}
          className={`flex items-center gap-2 px-8 py-4 rounded-2xl border-2 text-sm font-bold transition-all ${
            answered === true && isCorrect ? 'border-green-500 bg-green-500/20 text-green-400' :
            answered === true && !isCorrect ? 'border-red-500 bg-red-500/20 text-red-400' :
            answered !== null ? 'opacity-40 border-border/30' :
            'border-green-500/30 bg-green-500/5 text-green-400 hover:border-green-500/60 hover:bg-green-500/10 cursor-pointer'
          }`}
          data-testid="tf-true-btn">
          <ThumbsUp className="w-5 h-5" /> TRUE
        </motion.button>
        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
          onClick={() => handleAnswer(false)} disabled={answered !== null}
          className={`flex items-center gap-2 px-8 py-4 rounded-2xl border-2 text-sm font-bold transition-all ${
            answered === false && isCorrect ? 'border-green-500 bg-green-500/20 text-green-400' :
            answered === false && !isCorrect ? 'border-red-500 bg-red-500/20 text-red-400' :
            answered !== null ? 'opacity-40 border-border/30' :
            'border-red-500/30 bg-red-500/5 text-red-400 hover:border-red-500/60 hover:bg-red-500/10 cursor-pointer'
          }`}
          data-testid="tf-false-btn">
          <ThumbsDown className="w-5 h-5" /> FALSE
        </motion.button>
      </div>
    </div>
  );
}

// ========== COMPLETION CELEBRATION OVERLAY ==========
function CelebrationOverlay({ show }) {
  if (!show) return null;
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm pointer-events-none">
        <motion.div initial={{ scale: 0, rotate: -10 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          className="text-center pointer-events-auto">
          <motion.div animate={{ y: [0, -15, 0] }} transition={{ repeat: Infinity, duration: 2 }}
            className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 mx-auto mb-4 flex items-center justify-center shadow-2xl shadow-yellow-500/30">
            <Award className="w-14 h-14 text-white" />
          </motion.div>
          <h2 className="text-2xl font-bold mb-1">Lab Complete!</h2>
          <p className="text-muted-foreground text-sm">All missions completed. Amazing work!</p>
          {[...Array(8)].map((_, i) => (
            <motion.div key={i}
              initial={{ opacity: 1, y: 0, x: 0, scale: 1 }}
              animate={{ opacity: 0, y: -100 - Math.random() * 100, x: (Math.random() - 0.5) * 200, scale: 0.5 }}
              transition={{ duration: 1.5, delay: i * 0.1, ease: 'easeOut' }}
              className="absolute" style={{ left: `${20 + Math.random() * 60}%`, top: '50%' }}>
              <Sparkles className="w-5 h-5 text-yellow-400" />
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ========== MAIN LAB COMPONENT ==========
export default function InteractiveLab() {
  const navigate = useNavigate();
  const { labId } = useParams();
  const { user } = useAuth();
  const [currentMission, setCurrentMission] = useState(0);
  const [completedMissions, setCompletedMissions] = useState([]);
  const [xp, setXp] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showTeacher, setShowTeacher] = useState(false);
  const [teacherDismissed, setTeacherDismissed] = useState({});
  const resummonTeacher = () => { setShowTeacher(true); setTeacherDismissed(prev => ({ ...prev, [labId]: false })); };

  // Load saved progress on mount
  useEffect(() => {
    labProgressApi.getStats().then(data => {
      const labProgress = data?.per_lab?.[labId];
      if (labProgress) {
        const savedMissionIds = labProgress.completed_ids || [];
        const labMissions = ALL_LAB_CONTENT[labId]?.missions || [];
        const indices = savedMissionIds.map(mId => labMissions.findIndex(m => m.id === mId)).filter(i => i >= 0);
        setCompletedMissions(indices);
        setXp(labProgress.xp || 0);
      }
    }).catch(() => {});
  }, [labId]);

  // Show teacher avatar on first visit to a lab
  const teacherTimerRef = useRef(null);
  useEffect(() => {
    if (!ALL_LAB_CONTENT[labId]) return;
    if (teacherTimerRef.current) return;
    const key = `teacher-shown-${labId}`;
    if (!sessionStorage.getItem(key) && !teacherDismissed[labId]) {
      sessionStorage.setItem(key, '1');
      teacherTimerRef.current = setTimeout(() => setShowTeacher(true), 800);
    }
  }, [labId, teacherDismissed]);

  const labData = ALL_LAB_CONTENT[labId];
  if (!labData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-semibold mb-2">Lab not found</p>
          <Button onClick={() => navigate('/labs')} data-testid="back-to-labs-404">Back to Labs</Button>
        </div>
      </div>
    );
  }

  const mission = labData.missions[currentMission];
  const Icon = labData.icon;
  const totalCompleted = completedMissions.length;
  const totalMissions = labData.missions.length;

  const handleComplete = (gameStats) => {
    if (!completedMissions.includes(currentMission)) {
      const earnedXp = (mission?.stars || 1) * 25;
      setCompletedMissions(prev => {
        const next = [...prev, currentMission];
        setXp(x => x + earnedXp);
        if (next.length === totalMissions) {
          setTimeout(() => setShowCelebration(true), 500);
          setTimeout(() => setShowCelebration(false), 3500);
        }
        return next;
      });
      // Persist to backend
      const payload = {
        lab_id: labId,
        mission_id: mission.id,
        stars: mission?.stars || 1,
        xp_earned: earnedXp
      };
      if (gameStats && typeof gameStats === 'object') {
        payload.game_stats = gameStats;
      }
      labProgressApi.completeMission(payload).catch(() => {});
    }
  };

  const goNextMission = () => {
    if (currentMission < totalMissions - 1) setCurrentMission(currentMission + 1);
  };

  const renderActivity = () => {
    if (!mission) return null;
    const key = `${labId}-${currentMission}`;
    switch (mission.type) {
      case 'match': return <MatchActivity key={key} mission={mission} onComplete={handleComplete} />;
      case 'order': return <OrderActivity key={key} mission={mission} onComplete={handleComplete} />;
      case 'categorize': return <CategorizeActivity key={key} mission={mission} onComplete={handleComplete} />;
      case 'quiz': return <QuizActivity key={key} mission={mission} onComplete={handleComplete} />;
      case 'fill-blank': return <FillBlankActivity key={key} mission={mission} onComplete={handleComplete} />;
      case 'true-false': return <TrueFalseActivity key={key} mission={mission} onComplete={handleComplete} />;
      case 'timer-challenge': return <TimerChallengeActivity key={key} mission={mission} onComplete={handleComplete} />;
      case 'streak-combo': return <StreakComboActivity key={key} mission={mission} onComplete={handleComplete} />;
      case 'memory-card': return <MemoryCardActivity key={key} mission={mission} onComplete={handleComplete} />;
      case 'puzzle': return <PuzzleActivity key={key} mission={mission} onComplete={handleComplete} />;
      case 'rapid-fire': return <RapidFireActivity key={key} mission={mission} onComplete={handleComplete} />;
      case 'catch-game': return <CatchGameActivity key={key} mission={mission} onComplete={handleComplete} />;
      case 'target-shooter': return <TargetShooterActivity key={key} mission={mission} onComplete={handleComplete} />;
      default: return <p>Unknown activity type</p>;
    }
  };

  const difficultyLabel = (stars) => stars >= 3 ? 'Hard' : stars >= 2 ? 'Medium' : 'Easy';
  const difficultyColor = (stars) => stars >= 3 ? 'text-red-400' : stars >= 2 ? 'text-yellow-400' : 'text-green-400';

  return (
    <div className="min-h-screen bg-background" data-testid={`${labId}-lab`}>
      <CelebrationOverlay show={showCelebration} />
      <TeacherAvatar
        visible={showTeacher}
        avatarId={user?.avatar_id || 'male-1'}
        text={(() => {
          const m = labData.missions?.[currentMission];
          const mDesc = m ? (MISSION_DESCRIPTIONS?.[labId]?.[m.id] || m.description || '') : '';
          const allMissionTitles = (labData.missions || []).slice(0, 5).map(mi => mi.name).join(', ');
          return `Lab: ${labData.title}. ${labData.info || ''} This lab covers: ${allMissionTitles}. Current mission: ${m?.name || 'Introduction'}. ${mDesc}`;
        })()}
        cacheKey={`lab-intro-${labId}-m${currentMission}`}
        onClose={() => { setShowTeacher(false); setTeacherDismissed(prev => ({ ...prev, [labId]: true })); }}
        dismissed={!!teacherDismissed[labId]}
        onResummon={resummonTeacher}
      />

      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/labs')} data-testid="back-to-labs">
              <ArrowLeft className="w-4 h-4 mr-2" /> Labs
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <Icon className="w-5 h-5" style={{ color: labData.color }} />
              <span className="font-semibold">{labData.title}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-xs">{totalCompleted}/{totalMissions} done</Badge>
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/20">
              <Zap className="w-3.5 h-3.5 text-yellow-400" />
              <span className="text-xs font-bold text-yellow-400">{xp} XP</span>
            </div>
          </div>
        </div>
      </header>

      {/* Mission Navigator */}
      <div className="max-w-7xl mx-auto px-6 py-3 border-b border-border/30 overflow-x-auto">
        <div className="flex items-center gap-1.5 min-w-max">
          {labData.missions.map((m, i) => {
            const isCompleted = completedMissions.includes(i);
            const isCurrent = currentMission === i;
            return (
              <button key={m.id} onClick={() => setCurrentMission(i)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  isCurrent ? 'bg-primary/10 border border-primary/30 text-primary' :
                  isCompleted ? 'bg-green-500/10 border border-green-500/20 text-green-400' :
                  'bg-muted/30 border border-border/30 text-muted-foreground hover:border-primary/20'
                }`}
                data-testid={`mission-${m.id}`}>
                {isCompleted && <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />}
                <span>M{m.id}</span>
                <span className={`text-[10px] ${difficultyColor(m.stars || 1)}`}>{difficultyLabel(m.stars || 1)}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Mission Info */}
        <motion.div key={currentMission} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-3 mb-6 p-3 rounded-xl border"
          style={{ background: labData.color + '10', borderColor: labData.color + '30' }}>
          <Target className="w-5 h-5 shrink-0" style={{ color: labData.color }} />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm">{mission?.name}</span>
              <Badge variant="outline" className="text-[10px]">{mission?.type}</Badge>
            </div>
            <span className="text-xs text-muted-foreground">{mission?.description}</span>
          </div>
          <div className="flex gap-0.5">
            {[...Array(mission?.stars || 1)].map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${completedMissions.includes(currentMission) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`} />
            ))}
          </div>
        </motion.div>

        {/* Activity */}
        <AnimatePresence mode="wait">
          <motion.div key={`${labId}-${currentMission}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            {renderActivity()}
          </motion.div>
        </AnimatePresence>

        {/* Next mission hint */}
        {completedMissions.includes(currentMission) && currentMission < totalMissions - 1 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-4 rounded-xl bg-primary/5 border border-primary/20 flex items-center justify-between">
            <span className="text-sm font-medium">Mission complete! Ready for the next challenge?</span>
            <Button size="sm" onClick={goNextMission} data-testid="next-mission-btn">
              Next Mission <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </motion.div>
        )}

        {/* Lab info */}
        <div className="mt-8 p-4 rounded-xl bg-muted/30 border border-border/30">
          <h4 className="text-xs font-semibold flex items-center gap-1 mb-2 text-muted-foreground">
            <Info className="w-3 h-3" /> About This Lab
          </h4>
          <p className="text-xs text-muted-foreground leading-relaxed">{labData.info}</p>
        </div>
      </div>
    </div>
  );
}
